import React from 'react'
import { toast } from 'react-toastify'
import * as yup from 'yup'
import { useFormik } from 'formik'
import { useIntl } from 'react-intl'
import {
    Autocomplete,
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormHelperText,
    LinearProgress,
    SelectChangeEvent,
    TextField,
} from '@mui/material'
import { H6 } from '../../../../../layouts/TextStyles'
import CustomInput from '../../../../../components/muiComponents/customInput/CustomInput'
import SingleSelect from '../../../../../components/muiComponents/singleSelect'
import {
    isNumber,
    requiredId,
    requiredString,
} from '../../../../../utils/validation'
import { LessonScheduleState } from '../../../../../features/types'
import { lessonScheduleApiSlice } from '../../../../../features/schedule/lessonScheduleSlice'
import { groupApiSlice } from '../../../../../features/group/groupSlice'
import { useAppSelector } from '../../../../../app/hooks'
import { lang } from '../../../../../features/user/userSlice'
import { useParams } from 'react-router-dom'
import { tone, ui } from '../../../../../utils/uiTokens'

interface Props {
    editLesson?: LessonScheduleState
    open?: boolean
    handleClose?: () => void
}

const styles = {
    error: {
        color: tone.bad.text,
        marginLeft: 2,
    },
    fullWidth: {
        width: '100%',
    },

}

const AddLessonDialog: React.FC<Props> = ({
    editLesson = null,
    open = false,
    handleClose = () => { },
}) => {
    const { formatMessage } = useIntl()
    const { courseId } = useParams()
    const weekDays = [
        { id: 0, name: formatMessage({ id: 'monday' }) },
        { id: 1, name: formatMessage({ id: 'tuesday' }) },
        { id: 2, name: formatMessage({ id: 'wednesday' }) },
        { id: 3, name: formatMessage({ id: 'thursday' }) },
        { id: 4, name: formatMessage({ id: 'friday' }) },
        { id: 5, name: formatMessage({ id: 'saturday' }) },
        { id: 6, name: formatMessage({ id: 'sunday' }) },
    ]

    const [createLesson, { isLoading: isCreating }] =
        lessonScheduleApiSlice.useCreateScheduleLessonMutation()
    const [updateLesson, { isLoading: isUpdating }] =
        lessonScheduleApiSlice.useUpdateScheduleLessonMutation()

    const isLoading = isCreating || isUpdating

    const lessonSchedule: LessonScheduleState = {
        id: editLesson?.id ?? undefined,
        courseId: editLesson?.courseId ?? 0,
        studentGroupId: editLesson?.studentGroupId ?? 0,
        dayOfWeek: editLesson?.dayOfWeek ?? 0,
        startTime: editLesson?.startTime ?? '',
        endTime: editLesson?.endTime ?? '',
        // lessonUploadLink: editLesson?.lessonUploadLink ?? [],
        link: editLesson?.link ?? '',
    }

    const validationSchema = yup.object().shape({
        studentGroupId: requiredId({ formatMessage }),
        dayOfWeek: isNumber({ formatMessage }),
        startTime: requiredString({ formatMessage }),
        endTime: requiredString({ formatMessage }),
        // lessonUploadLink: yup.string(),
        link: requiredString({ formatMessage }),
    })

    const formik = useFormik({
        enableReinitialize: true,
        initialValues: lessonSchedule,
        validationSchema: validationSchema,
        validateOnChange: false,
        validateOnBlur: false,

        onSubmit: async () => {
            if (editLesson) {
                handleUpdateLesson()
            } else {
                handleCreateLesson()
            }
        },
    })

    const { data: groups = [], isFetching: isFetchingGroups } =
        groupApiSlice.useFetchAllGroupsQuery(Number(courseId), {
            skip: !open,
        })

    const language = useAppSelector(lang)

    const selectedGroup = groups.find(
        (group: any) => group.id === formik.values.studentGroupId,
    )

    const subjectValue = selectedGroup
        ? language === 'ru'
            ? selectedGroup.subjectsRu
            : selectedGroup.subjectsKz
        : ''

    const handleCreateLesson = async () => {
        try {
            await createLesson({ body: formik.values }).unwrap()
            toast.success(formatMessage({ id: 'savedSuccess' }))
            formik.resetForm()
            handleClose()
        } catch (error) { }
    }

    const handleUpdateLesson = async () => {
        try {
            await updateLesson({
                id: editLesson!.id!,
                body: formik.values,
            }).unwrap()
            toast.success(formatMessage({ id: 'savedSuccess' }))
            handleClose()
        } catch (error) { }
    }

    const handleGroupChange = (event: any, newValue: any) => {
        if (newValue) {
            formik.setFieldValue('studentGroupId', newValue.id)
            formik.setFieldValue('courseId', newValue.courseId)
        } else {
            formik.setFieldValue('studentGroupId', 0)
            formik.setFieldValue('courseId', 0)
        }
    }

    const handleDayOfWeekChange = (
        event: SelectChangeEvent<typeof formik.values.dayOfWeek>,
    ) => {
        const {
            target: { value },
        } = event
        formik.setFieldValue('dayOfWeek', value)
    }


    return (
        <Dialog
            maxWidth={'md'}
            fullWidth
            open={open ?? false}
            onClose={handleClose}
        >
            <DialogTitle>
                {editLesson
                    ? formatMessage({ id: 'editData' })
                    : formatMessage({ id: 'add' })}
            </DialogTitle>

            <DialogContent>
                {isFetchingGroups && <LinearProgress />}
                <form onSubmit={formik.handleSubmit}>
                    <Box >
                        <Box sx={{ display: 'flex', gap: 2 }}>
                            <Box mb={2} sx={{ width: '100%' }}>
                                <H6>{formatMessage({ id: 'group' })}</H6>

                                <Autocomplete
                                    sx={{ mb: 1, mt: 2 }}
                                    id="studentGroupId"
                                    options={groups}
                                    getOptionLabel={(option: any) =>
                                        option.title || ''
                                    }
                                    value={
                                        groups.find(
                                            (g: any) =>
                                                g.id === formik.values.studentGroupId,
                                        ) || null
                                    }
                                    onChange={handleGroupChange}
                                    onBlur={formik.handleBlur}
                                    loading={isFetchingGroups}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            placeholder={formatMessage({
                                                id: 'group',
                                            })}
                                            error={Boolean(
                                                formik.errors.studentGroupId,
                                            )}
                                            sx={{
                                                '& .MuiOutlinedInput-root': {
                                                    backgroundColor:
                                                        ui.track,
                                                    borderRadius: '8px',
                                                },
                                            }}
                                        />
                                    )}
                                    renderOption={(props, option: any) => (
                                        <Box
                                            component="li"
                                            {...props}
                                            key={option.id}
                                        >
                                            {option.title}
                                        </Box>
                                    )}
                                />

                                <FormHelperText sx={styles.error}>
                                    {formik.touched.studentGroupId &&
                                        formik.errors.studentGroupId}
                                </FormHelperText>
                            </Box>

                            <Box mb={2} sx={{ width: '100%' }}>
                                <H6>
                                    {formatMessage({ id: 'subjects' }) ||
                                        'Subject'}
                                </H6>

                                <CustomInput
                                    id="subject"
                                    name="subject"
                                    value={subjectValue}
                                    disabled
                                    fullWidth
                                    placeholder={formatMessage({
                                        id: 'subjects',
                                    })}
                                />
                            </Box>
                        </Box>
                        <Box sx={styles.fullWidth} mr={4}>

                            <Box mb={2}>
                                <H6>
                                    {formatMessage({ id: 'dayOfWeek' })}
                                </H6>

                                <SingleSelect
                                    id="dayOfWeek"
                                    name="dayOfWeek"
                                    value={formik.values.dayOfWeek}
                                    handleChange={handleDayOfWeekChange}
                                    handleBlur={formik.handleBlur}
                                    menuItems={weekDays}
                                    placeholder={weekDays[formik.values.dayOfWeek].name || 'Select day'}
                                    handleError={Boolean(
                                        formik.errors.dayOfWeek,
                                    )}
                                    valueField="id"
                                />

                                <FormHelperText sx={styles.error}>
                                    {formik.touched.dayOfWeek &&
                                        formik.errors.dayOfWeek}
                                </FormHelperText>
                            </Box>
                        </Box>

                        <Box sx={{ display: 'flex', gap: 2 }}>
                            <Box mb={2} sx={{ width: '100%' }}>
                                <H6>
                                    {formatMessage({ id: 'startDateTime' })}
                                </H6>

                                <CustomInput
                                    id="startTime"
                                    name="startTime"
                                    type="time"
                                    value={formik.values.startTime}
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    error={Boolean(formik.errors.startTime)}
                                    helperText={formik.errors.startTime}
                                    placeholder={formatMessage({
                                        id: 'startTime',
                                    }) || '11:00'}
                                    fullWidth
                                    InputLabelProps={{ shrink: true }}
                                />
                            </Box>

                            <Box mb={2} sx={{ width: '100%' }}>
                                <H6>
                                    {formatMessage({ id: 'endDateTime' })}
                                </H6>

                                <CustomInput
                                    id="endTime"
                                    name="endTime"
                                    type="time"
                                    value={formik.values.endTime}
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    error={Boolean(formik.errors.endTime)}
                                    helperText={formik.errors.endTime}
                                    placeholder={formatMessage({
                                        id: 'endTime',
                                    }) || '13:00'}
                                    fullWidth
                                    InputLabelProps={{ shrink: true }}
                                />
                            </Box>
                        </Box>
                        <Box sx={{ display: 'flex', gap: 2 }}>

                            <Box mb={2} sx={{ width: '100%' }}>
                                <H6>
                                    {formatMessage({ id: 'link' })}
                                </H6>

                                <CustomInput
                                    id="link"
                                    name="link"
                                    value={formik.values.link}
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    error={Boolean(formik.errors.link)}
                                    helperText={formik.errors.link}
                                    placeholder={formatMessage({
                                        id: 'link',
                                    }) || 'https://...'}
                                    fullWidth
                                />
                            </Box>
                        </Box>
                    </Box>

                    <DialogActions>
                        <Button
                            onClick={handleClose}
                            variant="outlined"
                            disabled={isLoading}
                        >
                            {formatMessage({ id: 'cancel' })}
                        </Button>
                        <Button
                            onClick={() => formik.handleSubmit()}
                            variant="contained"
                            disabled={isLoading}
                        >
                            {isLoading ? (
                                <CircularProgress
                                    size={20}
                                    style={{ marginRight: '8px' }}
                                />
                            ) : editLesson ? (
                                formatMessage({ id: 'update' })
                            ) : (
                                formatMessage({ id: 'add' })
                            )}
                        </Button>
                    </DialogActions>
                </form>
            </DialogContent>
        </Dialog>
    )
}

export default AddLessonDialog
